import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Input } from 'antd';
import { MagnifyingGlass, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { ButtonCommon, CategoryCheckboxList } from 'components/common';
import { CategorySelectionSectionProps, CategoryItem } from 'types';

const CategorySelectionSection: React.FC<CategorySelectionSectionProps> = ({
  form,
  categories,
  availableCategories,
  setAvailableCategories,
  selectedCategories,
  setSelectedCategories,
  searchValue,
  setSearchValue,
  editMode,
  isOpen,
  checkedAvailableIds,
  setCheckedAvailableIds,
}) => {
  const [checkedSelectedIds, setCheckedSelectedIds] = useState<string[]>([]);

  // Pagination state for flat list
  const [availablePagination, setAvailablePagination] = useState({
    current: 1,
    pageSize: 8,
    total: 0,
  });

  const [selectedPagination, setSelectedPagination] = useState({
    current: 1,
    pageSize: 8,
    total: 0,
  });

  // Reset all states every time modal opens
  useEffect(() => {
    if (isOpen) {
      setCheckedAvailableIds([]);
      setCheckedSelectedIds([]);
      setSearchValue('');
      if (!editMode) {
        setAvailableCategories(categories);
        setSelectedCategories([]);
      } else {
        const formValues = form.getFieldsValue(true);
        const categoryIds = formValues.categories || [];
        let selectedCats: CategoryItem[] = [];
        if (formValues.categoryObjects && formValues.categoryObjects.length > 0) {
          selectedCats = formValues.categoryObjects.map((cat: CategoryItem) => {
            const fullCat = categories.find((c) => c.id === cat.id);
            return fullCat ? { ...fullCat } : cat;
          });
        } else if (categoryIds.length > 0) {
          selectedCats = categoryIds.map((id: string) => {
            const cat = categories.find((c) => c.id === String(id));
            return cat || ({ id: String(id), name: `Category ${id}` } as CategoryItem);
          });
        }
        setSelectedCategories(selectedCats);

        const selectedIds = new Set(selectedCats.map((cat) => cat.id));
        setAvailableCategories(categories.filter((cat) => !selectedIds.has(cat.id)));
      }
    }
  }, [isOpen, editMode, categories, form, setAvailableCategories, setSelectedCategories, setSearchValue]);

  // Filter available categories by search (flat, no parent/child)
  const filteredAvailableCategories = useMemo(() => {
    if (!searchValue) return availableCategories;
    return availableCategories.filter((cat) => cat.name.toLowerCase().includes(searchValue.toLowerCase()));
  }, [availableCategories, searchValue]);

  useEffect(() => {
    setAvailablePagination((prev) => ({
      ...prev,
      total: filteredAvailableCategories.length,
      current: prev.current > Math.ceil(filteredAvailableCategories.length / prev.pageSize) ? 1 : prev.current,
    }));
  }, [filteredAvailableCategories]);

  useEffect(() => {
    setSelectedPagination((prev) => ({
      ...prev,
      total: selectedCategories.length,
      current: prev.current > Math.ceil(selectedCategories.length / prev.pageSize) ? 1 : prev.current,
    }));
  }, [selectedCategories]);

  const handleAvailablePageChange = useCallback((page: number, pageSize?: number) => {
    setAvailablePagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    setCheckedAvailableIds([]);
  }, []);

  const handleSelectedPageChange = useCallback((page: number, pageSize?: number) => {
    setSelectedPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
    setCheckedSelectedIds([]);
  }, []);

  // Get paginated available categories (flat)
  const getAvailableItems = useCallback(() => {
    const start = (availablePagination.current - 1) * availablePagination.pageSize;
    const end = start + availablePagination.pageSize;
    return filteredAvailableCategories.slice(start, end);
  }, [filteredAvailableCategories, availablePagination]);

  // Get paginated selected categories (flat)
  const getSelectedItems = useCallback(() => {
    const start = (selectedPagination.current - 1) * selectedPagination.pageSize;
    const end = start + selectedPagination.pageSize;
    return selectedCategories.slice(start, end);
  }, [selectedCategories, selectedPagination]);

  // Select all available
  const handleSelectAllAvailable = (checked: boolean) => {
    if (checked) {
      setCheckedAvailableIds(getAvailableItems().map((cat) => cat.id));
    } else {
      setCheckedAvailableIds([]);
    }
  };

  // Select all selected
  const handleSelectAllSelected = (checked: boolean) => {
    if (checked) {
      setCheckedSelectedIds(getSelectedItems().map((cat) => cat.id));
    } else {
      setCheckedSelectedIds([]);
    }
  };

  // Transfer to selected (flat)
  const handleTransferToSelected = useCallback(() => {
    if (checkedAvailableIds.length === 0) return;
    const toTransfer = availableCategories.filter((cat) => checkedAvailableIds.includes(cat.id));
    setSelectedCategories([...selectedCategories, ...toTransfer]);
    setAvailableCategories(availableCategories.filter((cat) => !checkedAvailableIds.includes(cat.id)));
    form.setFieldValue(
      'categories',
      [...selectedCategories, ...toTransfer].map((cat) => String(cat.id)),
    );
    setCheckedAvailableIds([]);
    setAvailablePagination((prev) => ({
      ...prev,
      total: availableCategories.length - checkedAvailableIds.length,
      current: 1,
    }));
    setSelectedPagination((prev) => ({
      ...prev,
      total: selectedCategories.length + checkedAvailableIds.length,
      current: 1,
    }));
  }, [
    checkedAvailableIds,
    availableCategories,
    selectedCategories,
    form,
    setAvailableCategories,
    setSelectedCategories,
  ]);

  // Transfer to available (flat)
  const handleTransferToAvailable = useCallback(() => {
    if (checkedSelectedIds.length === 0) return;
    const toTransfer = selectedCategories.filter((cat) => checkedSelectedIds.includes(cat.id));
    setAvailableCategories([...availableCategories, ...toTransfer]);
    setSelectedCategories(selectedCategories.filter((cat) => !checkedSelectedIds.includes(cat.id)));
    form.setFieldValue(
      'categories',
      selectedCategories.filter((cat) => !checkedSelectedIds.includes(cat.id)).map((cat) => String(cat.id)),
    );
    setCheckedSelectedIds([]);
    setAvailablePagination((prev) => ({
      ...prev,
      total: availableCategories.length + checkedSelectedIds.length,
      current: 1,
    }));
    setSelectedPagination((prev) => ({
      ...prev,
      total: selectedCategories.length - checkedSelectedIds.length,
      current: 1,
    }));
  }, [
    checkedSelectedIds,
    availableCategories,
    selectedCategories,
    form,
    setAvailableCategories,
    setSelectedCategories,
  ]);

  return (
    <div className='col-span-12 p-5 bg-white rounded-md shadow-sm h-full'>
      <h3 className='text-[20px] leading-[24px] font-bold mb-4'>Thêm danh mục vào thương hiệu</h3>

      <div className='relative my-4'>
        <Input
          placeholder='Tìm danh mục'
          prefix={<MagnifyingGlass size={18} weight='bold' className='mr-2' />}
          className='rounded-md'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <div className='grid grid-cols-[repeat(21,_minmax(0,_1fr))] gap-3 h-[calc(100%-100px)] sm:h-[250px] md:h-[300px] lg:h-[350px] xl:h-[400px] 2xl:h-[500px] min-[1800px]:h-[50vh] '>
        <div className='col-span-10'>
          <CategoryCheckboxList
            title='Danh sách danh mục'
            categories={getAvailableItems()}
            checkedIds={checkedAvailableIds}
            onCheckedIdsChange={setCheckedAvailableIds}
            onSelectAll={handleSelectAllAvailable}
            selectionCount={checkedAvailableIds.length}
            totalCount={filteredAvailableCategories.length}
            className='h-full'
            pagination={{
              current: availablePagination.current,
              pageSize: availablePagination.pageSize,
              total: filteredAvailableCategories.length,
            }}
            onPageChange={handleAvailablePageChange}
            allCategories={[]}
          />
        </div>

        <div className='col-span-1 flex flex-col items-center justify-center gap-4'>
          <ButtonCommon
            color='outline'
            radius='rounded'
            className='w-[40px] h-[40px] !p-[8px]'
            onClick={handleTransferToSelected}
            disabled={checkedAvailableIds.length === 0}
          >
            <CaretRight size={20} />
          </ButtonCommon>

          <ButtonCommon
            color='outline'
            radius='rounded'
            className='w-[40px] h-[40px] !p-[8px]'
            onClick={handleTransferToAvailable}
            disabled={checkedSelectedIds.length === 0}
          >
            <CaretLeft size={20} />
          </ButtonCommon>
        </div>

        <div className='col-span-10'>
          <CategoryCheckboxList
            title='Danh mục đã thêm'
            categories={getSelectedItems()}
            checkedIds={checkedSelectedIds}
            onCheckedIdsChange={setCheckedSelectedIds}
            onSelectAll={handleSelectAllSelected}
            selectionCount={checkedSelectedIds.length}
            totalCount={selectedCategories.length}
            className='h-full'
            pagination={{
              current: selectedPagination.current,
              pageSize: selectedPagination.pageSize,
              total: selectedCategories.length,
            }}
            onPageChange={handleSelectedPageChange}
            allCategories={[]}
          />
        </div>
      </div>
    </div>
  );
};

export default CategorySelectionSection;
